import { useState, useMemo } from "react"
import moment from "moment"
import { X, Check, TrendingDown, AlertTriangle } from "lucide-react"

import { ROOM_TYPES } from "../../../../utils/exports/room-types"
import { BOARD_TYPES } from "../../../../utils/exports/board-types"
import { FEE_TYPES } from "../../../../assets/data/exports"
import { calculatePackageCost } from "../../../quoting/utils/package-cost"

import Badge from "../../../../components/ui/badge/badge"
import Button from "../../../../components/ui/button/button"
import Input from "../../../../components/ui/inputs/input"
import SearchSelect from "../../../../components/ui/select/search-select"
import Stepper from "../../../../components/ui/stepper/stepper"

// Default occupancy inferred from a room type, mirroring the builder's own
// helper (kept local to avoid a circular import with new-quote.js). Room rates
// are per person, so a room costs rate x sleeps; the agent can override per row.
const defaultSleeps = (key) => {
	if (typeof key !== "string") return 2
	if (key.includes("single")) return 1
	if (key.includes("twin") || key.includes("double")) return 2
	if (key.includes("triple") || key.includes("three")) return 3
	if (key.includes("quad") || key.includes("four")) return 4
	if (key.includes("five")) return 5
	if (key.includes("six")) return 6
	if (key.includes("seven")) return 7
	return 2
}

// A window covers a date when the date falls within its [start, end] range.
// With no date to match against we show everything (defensive fallback).
const spansDate = (window, millis) => {
	if (!millis) return true
	const day = moment(millis).startOf("day").unix()
	return window.start_date?.seconds <= day && window.end_date?.seconds >= day
}

// When no live rates are loaded for the requested date, we look back this many
// years for the same dates and present those rates with an uplift applied, as a
// rough estimate of where this year's price might land. Exported so the costs
// column can word its matching warning from the same single source.
export const PREDICTION_LOOKBACK_YEARS = 1
export const PREDICTION_UPLIFT_PCT = 15

// Multiply a cost (stored as a number-ish string) by the prediction uplift,
// rounded to whole currency units to match how rates are entered. Empty / zero
// costs (e.g. an included board) are left untouched.
const applyUplift = (cost) => {
	const value = Number(cost)
	if (!value) return cost
	return Math.round(value * (1 + PREDICTION_UPLIFT_PCT / 100))
}

// Clone a year-prior window with the uplift baked into every cost field and a
// `predicted` tag. Baking it in means the modal rows, calculatePackageCost and
// the costs column all read the estimated figures with no special-casing - only
// the tag (and the date it came from) needs to travel for the warnings.
const toPredictedWindow = (window, fromMillis) => ({
	...window,
	predicted: true,
	predictedFromMillis: fromMillis,
	upliftPct: PREDICTION_UPLIFT_PCT,
	price_lowest: applyUplift(window.price_lowest),
	price_highest: applyUplift(window.price_highest),
	room_types: (window.room_types || []).map((room) => ({ ...room, cost: applyUplift(room.cost) })),
	board_types: (window.board_types || []).map((board) => ({ ...board, cost: applyUplift(board.cost) })),
	fee_types: (window.fee_types || []).map((fee) => ({ ...fee, cost: applyUplift(fee.cost) })),
})

// Single-occupancy room types are the "_single" variants (no separate field).
const isSingleRoom = (key) => typeof key === "string" && key.includes("single")

const windowDateRange = (window) => {
	const from = window.start_date?.seconds ? moment(window.start_date.seconds, "X").format("DD MMM YYYY") : "?"
	const to = window.end_date?.seconds ? moment(window.end_date.seconds, "X").format("DD MMM YYYY") : "?"
	return `${from} - ${to}`
}

/**
 * Package-selection modal. Lists the real price windows loaded on the system
 * for the chosen hotel/course that are valid for the relevant date (a hotel's
 * check-in, a round's tee-time). Per-person prices are shown here to help the
 * agent choose; the itinerary preview itself stays price-free.
 *
 * Hotels: filter chips (room type / board type / single occupancy) narrow the
 * list; selecting a package drives the basket item.
 * Courses: each window's green-fee bands (Regular / Twilight / ...) are the
 * selectable options.
 */
export default function PackageModal({ item, matchMillis, flexWeeks = 0, currencySymbol = "£", onClose, onSelect, onApplyDate }) {
	const isHotel = item.type === "hotel"

	const windows = useMemo(
		() => (item.priceWindows || []).filter((window) => !window.is_draft && spansDate(window, matchMillis)),
		[item.priceWindows, matchMillis]
	)

	// Year-prior fallback: only computed (and only used) when no live rates cover
	// the requested date. The same dates a year earlier, uplifted, stand in as an
	// estimate of this year's pricing.
	const priorMillis = useMemo(() => (matchMillis ? moment(matchMillis).subtract(PREDICTION_LOOKBACK_YEARS, "years").valueOf() : 0), [matchMillis])
	const predictedWindows = useMemo(() => {
		if (!matchMillis || windows.length > 0) return []
		return (item.priceWindows || []).filter((window) => !window.is_draft && spansDate(window, priorMillis)).map((window) => toPredictedWindow(window, priorMillis))
	}, [item.priceWindows, matchMillis, windows.length, priorMillis])

	// When live rates are missing but a year-prior estimate exists, the rest of
	// the modal renders from that estimate instead.
	const isPredicted = windows.length === 0 && predictedWindows.length > 0
	const displayWindows = isPredicted ? predictedWindows : windows

	// Packages are always filtered to the item's nights (per-night packages fit
	// any length); this is surfaced in the subheader rather than as a chip.
	const requestedNights = Number(item.nights) || 0
	const [roomFilter, setRoomFilter] = useState([])
	const [boardFilter, setBoardFilter] = useState([])
	const [singleOnly, setSingleOnly] = useState(false)

	// When the item already holds a CUSTOM package, open straight into the builder
	// pre-filled from the saved selection (rooms/board/fees + net costs) so editing
	// loads the values rather than starting blank. Each room re-reads its own rate
	// off the saved window (older custom packages shared one rate across the mix,
	// so every room simply pre-fills with that shared figure).
	const customPkg = item.selectedPackage?.is_custom ? item.selectedPackage : null
	const initialCustom = customPkg
		? {
				nights: item.nights,
				rounds: customPkg.rounds,
				rooms: (item.selectedRooms || []).map((room) => ({
					key: room.key,
					qty: room.qty,
					sleeps: room.sleeps,
					cost: (customPkg.room_types || []).find((roomType) => roomType.type === room.key)?.cost,
				})),
				boardType: item.selectedBoard || "",
				feeType: item.chosenFeeType || "",
				netCost: isHotel ? undefined : customPkg.fee_types?.[0]?.cost,
		  }
		: null

	// Two views in this modal: "packages" lists the loaded price windows; "custom"
	// hand-builds a one-off package (mirrors the ad-hoc "enter pricing details"
	// block on the /quoting page). Default to custom when editing a custom one.
	const [mode, setMode] = useState(customPkg ? "custom" : "packages")

	// Build the agent's hand-entered package as a custom price window and hand it
	// back through the same onSelect path a loaded window uses, so the basket item
	// (costing, room/board pickers, preview, save) treats it identically. The
	// window is shaped exactly like a real one - just tagged `is_custom`/`custom`
	// so it's never multiplied as a per-night package.
	const handleAddCustom = (values) => {
		const randomId = () => Math.random().toString(36).substr(2, 9)
		const startSeconds = matchMillis ? moment(matchMillis).startOf("day").unix() : moment().startOf("day").unix()

		if (isHotel) {
			const nights = Number(values.nights) || 0
			const endSeconds = moment.unix(startSeconds).add(nights, "days").endOf("day").unix()
			// Each chosen room type carries its OWN per-person net rate, exactly like
			// a loaded package's room_types - the per-room quantity/sleeps then drive
			// capacity and the costing engine reads the rate per room key.
			onSelect({
				window: {
					id: `custom-${randomId()}`,
					is_custom: true,
					is_draft: false,
					price_type: "resort",
					type: "custom",
					name: "Custom package",
					nights,
					rounds: Number(values.rounds) || 0,
					room_types: (values.rooms || []).map((room, index) => ({ type: room.key, cost: Number(room.cost) || 0, base: index === 0, _id: randomId() })),
					board_types: values.boardType ? [{ type: values.boardType, cost: 0, base: true, _id: randomId() }] : [],
					start_date: { seconds: startSeconds, nanoseconds: 0 },
					end_date: { seconds: endSeconds, nanoseconds: 0 },
				},
				rooms: (values.rooms || []).map((room) => ({ key: room.key, qty: Number(room.qty) || 1, sleeps: Number(room.sleeps) || defaultSleeps(room.key) })),
			})
			return
		}

		onSelect({
			window: {
				id: `custom-${randomId()}`,
				is_custom: true,
				is_draft: false,
				price_type: "course",
				type: "custom",
				name: "Custom green fees",
				fee_types: [{ type: values.feeType, cost: Number(values.netCost) || 0, base: true, _id: randomId() }],
				start_date: { seconds: startSeconds, nanoseconds: 0 },
				end_date: { seconds: startSeconds, nanoseconds: 0 },
			},
			feeType: values.feeType,
		})
	}

	// Set after the agent accepts a cheaper check-in, so we can show the
	// old-vs-new price confirmation.
	const [appliedNote, setAppliedNote] = useState(null)

	// Distinct room / board chips across the valid windows.
	const roomChips = useMemo(() => {
		const map = new Map()
		displayWindows.forEach((window) => (window.room_types || []).forEach((room) => room.type && map.set(room.type, ROOM_TYPES[room.type] || room.type)))
		return Array.from(map, ([key, label]) => ({ key, label })).sort((a, b) => a.label.localeCompare(b.label))
	}, [displayWindows])

	const boardChips = useMemo(() => {
		const map = new Map()
		displayWindows.forEach((window) => (window.board_types || []).forEach((board) => board.type && map.set(board.type, BOARD_TYPES[board.type] || board.type)))
		return Array.from(map, ([key, label]) => ({ key, label })).sort((a, b) => a.label.localeCompare(b.label))
	}, [displayWindows])

	const hasSingleOption = useMemo(() => displayWindows.some((window) => (window.room_types || []).some((room) => isSingleRoom(room.type))), [displayWindows])

	const toggle = (list, setList, key) => setList(list.includes(key) ? list.filter((value) => value !== key) : [...list, key])

	const filteredWindows = useMemo(() => {
		let list = displayWindows
		if (isHotel) {
			list = list.filter((window) => {
				// Per-night packages fit any length, so they're never excluded by nights.
				if (requestedNights > 0 && window.type !== "per_night" && Number(window.nights) !== requestedNights) return false
				if (roomFilter.length && !(window.room_types || []).some((room) => roomFilter.includes(room.type))) return false
				if (boardFilter.length && !(window.board_types || []).some((board) => boardFilter.includes(board.type))) return false
				if (singleOnly && !(window.room_types || []).some((room) => isSingleRoom(room.type))) return false
				return true
			})
		}
		// Sort the displayed packages alphabetically by name.
		return [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""))
	}, [isHotel, displayWindows, requestedNights, roomFilter, boardFilter, singleOnly])

	// Cheaper-date suggestion: with date flexibility on, scan +/- weeks for a
	// nearby check-in where the same package (same nights/kind) on the held
	// room/board (active filters, else cheapest) lands in a cheaper rate window.
	const cheaperAlternative = useMemo(() => {
		// Skipped while predicting - there's no live rate to flex around, and the
		// scan would compare uplifted estimates against raw prior-year rates.
		if (isPredicted || !isHotel || !flexWeeks || flexWeeks < 1 || !matchMillis || filteredWindows.length === 0) return null

		// Per-person price for a window on a specific room + board.
		const priceFor = (window, roomType, boardType) => {
			const costPkg = window.type === "per_night" ? { ...window, nights: requestedNights } : window
			return calculatePackageCost(costPkg, { chosenRoomType: { option: roomType }, chosenBoardType: { option: boardType } })
		}
		// Cheapest room/board honouring the active filters (else cheapest overall).
		const cheapestOf = (items, allowed, single) => {
			let pool = allowed.length ? items.filter((entry) => allowed.includes(entry.type)) : items
			if (single) {
				const singles = pool.filter((entry) => isSingleRoom(entry.type))
				if (singles.length) pool = singles
			}
			return pool.reduce((best, entry) => ((Number(entry.cost) || 0) < (Number(best?.cost) || Infinity) ? entry : best), null)
		}

		// Baseline: cheapest window + held room/board at the current check-in.
		let baseline = null
		filteredWindows.forEach((window) => {
			const room = cheapestOf(window.room_types || [], roomFilter, singleOnly)
			if (!room) return
			const board = cheapestOf(window.board_types || [], boardFilter, false)
			const price = priceFor(window, room.type, board?.type)
			if (baseline === null || price < baseline.price) baseline = { window, roomType: room.type, boardType: board?.type, price }
		})
		if (!baseline) return null

		// Scan the window for the same room/board, same nights/kind, cheaper.
		// Baseline fields hoisted to consts so the inner checks stay closure-free.
		const allWindows = (item.priceWindows || []).filter((window) => !window.is_draft)
		const offsetRange = flexWeeks * 7
		const baseRoom = baseline.roomType
		const baseBoard = baseline.boardType
		const baseType = baseline.window.type
		const baseNights = Number(baseline.window.nights)
		let best = null
		for (let offset = -offsetRange; offset <= offsetRange; offset++) {
			if (offset === 0) continue
			const candidateMoment = moment(matchMillis).clone().add(offset, "days")
			const candSeconds = candidateMoment.clone().startOf("day").unix()
			const candidateMillis = candidateMoment.clone().startOf("day").valueOf()
			for (const window of allWindows) {
				if (!(window.start_date?.seconds <= candSeconds && window.end_date?.seconds >= candSeconds)) continue
				if (window.type !== baseType) continue
				if (baseType === "pre_defined" && Number(window.nights) !== baseNights) continue
				if (!(window.room_types || []).some((room) => room.type === baseRoom)) continue
				if (baseBoard && !(window.board_types || []).some((board) => board.type === baseBoard)) continue
				const price = priceFor(window, baseRoom, baseBoard)
				if (best === null || price < best.price) best = { price, millis: candidateMillis, offset }
			}
		}

		if (best && Math.round(best.price) < Math.round(baseline.price)) {
			return { oldPrice: baseline.price, newPrice: best.price, saving: baseline.price - best.price, millis: best.millis, offsetDays: best.offset }
		}
		return null
	}, [isPredicted, isHotel, flexWeeks, matchMillis, item.priceWindows, filteredWindows, requestedNights, roomFilter, boardFilter, singleOnly])

	const matchLabel = matchMillis ? moment(matchMillis).format("ddd D MMM YYYY") : null
	const priorLabel = priorMillis ? moment(priorMillis).format("ddd D MMM YYYY") : null

	return (
		<div className="package-modal-overlay">
			<div className={["package-modal", mode === "custom" ? "is-custom" : ""].join(" ").trim()} role="dialog" aria-modal="true" aria-label="Select a package">
				<div className="package-modal-header">
					<div>
						<h3>Select a package</h3>
						<p>
							{item.title}
							{matchLabel ? <> · valid for <strong>{matchLabel}</strong></> : null}
							{isHotel && requestedNights > 0 ? <> · filtering <strong>{requestedNights} night</strong> packages</> : null}
						</p>
					</div>
					<button type="button" className="package-modal-close" onClick={onClose} aria-label="Close">
						<X size={18} />
					</button>
				</div>

				{/* Pick from the loaded packages, or hand-build a one-off custom one. */}
				<div className="package-modal-tabs">
					<button type="button" className={["package-modal-tab", mode === "packages" ? "is-active" : ""].join(" ").trim()} onClick={() => setMode("packages")}>
						Choose package
					</button>
					<button type="button" className={["package-modal-tab", mode === "custom" ? "is-active" : ""].join(" ").trim()} onClick={() => setMode("custom")}>
						Create custom package
					</button>
				</div>

				{mode === "custom" ? (
					<CustomPackageForm isHotel={isHotel} currencySymbol={currencySymbol} requestedNights={requestedNights} initial={initialCustom} onCancel={() => setMode("packages")} onAdd={handleAddCustom} />
				) : (
				<>
				{/* Cheaper-date helper (positive), sat between the header divider and
				    its own bottom divider, above the filter chips. */}
				{appliedNote ? (
					<div className="package-modal-alert">
						<Check size={16} />
						<p>
							Check-in moved to <strong>{moment(appliedNote.millis).format("ddd D MMM YYYY")}</strong> - now {currencySymbol}
							{Math.round(appliedNote.newPrice).toLocaleString()}pp <span className="package-modal-alert-was">(was {currencySymbol}{Math.round(appliedNote.oldPrice).toLocaleString()}pp)</span>
						</p>
					</div>
				) : cheaperAlternative ? (
					<div className="package-modal-alert">
						<TrendingDown size={16} />
						<p>
							Travelling on <strong>{moment(cheaperAlternative.millis).format("ddd D MMM YYYY")}</strong> ({Math.abs(cheaperAlternative.offsetDays)}{" "}
							{Math.abs(cheaperAlternative.offsetDays) === 1 ? "day" : "days"} {cheaperAlternative.offsetDays > 0 ? "later" : "earlier"}) is cheaper - the same package is{" "}
							{currencySymbol}
							{Math.round(cheaperAlternative.newPrice).toLocaleString()}pp instead of {currencySymbol}
							{Math.round(cheaperAlternative.oldPrice).toLocaleString()}pp (saving {currencySymbol}
							{Math.round(cheaperAlternative.saving).toLocaleString()}pp).
						</p>
						<Button
							small
							label="Use this date"
							onClick={() => {
								setAppliedNote({ oldPrice: cheaperAlternative.oldPrice, newPrice: cheaperAlternative.newPrice, millis: cheaperAlternative.millis })
								if (onApplyDate) onApplyDate(cheaperAlternative.millis)
							}}
						/>
					</div>
				) : isPredicted ? (
					<div className="package-modal-alert is-warning">
						<AlertTriangle size={16} />
						<p>
							No live rates are loaded for <strong>{matchLabel}</strong>. The prices below are taken from <strong>{priorLabel}</strong> (the same dates
							a year earlier) with a {PREDICTION_UPLIFT_PCT}% increase applied as an estimate of this year's rates - treat them as a guide.
						</p>
					</div>
				) : null}

				{/* Hotel filter chips, grouped by type onto their own rows */}
				{isHotel && displayWindows.length > 0 && (hasSingleOption || roomChips.length > 0 || boardChips.length > 0) && (
					<div className="package-modal-filters">
						{hasSingleOption && (
							<div className="package-filter-group">
								<span className="package-filter-label">Occupancy</span>
								<div className="package-filter-chips">
									<button type="button" className={["package-chip", singleOnly ? "is-active" : ""].join(" ").trim()} onClick={() => setSingleOnly((value) => !value)}>
										Single occupancy
									</button>
								</div>
							</div>
						)}

						{roomChips.length > 0 && (
							<div className="package-filter-group">
								<span className="package-filter-label">Room type</span>
								<div className="package-filter-chips">
									{roomChips.map((chip) => (
										<button key={`room-${chip.key}`} type="button" className={["package-chip", roomFilter.includes(chip.key) ? "is-active" : ""].join(" ").trim()} onClick={() => toggle(roomFilter, setRoomFilter, chip.key)}>
											{chip.label}
										</button>
									))}
								</div>
							</div>
						)}

						{boardChips.length > 0 && (
							<div className="package-filter-group">
								<span className="package-filter-label">Board type</span>
								<div className="package-filter-chips">
									{boardChips.map((chip) => (
										<button key={`board-${chip.key}`} type="button" className={["package-chip", boardFilter.includes(chip.key) ? "is-active" : ""].join(" ").trim()} onClick={() => toggle(boardFilter, setBoardFilter, chip.key)}>
											{chip.label}
										</button>
									))}
								</div>
							</div>
						)}
					</div>
				)}

				<div className="package-modal-body">
					{displayWindows.length === 0 ? (
						<div className="package-modal-empty">
							No packages are loaded for {matchLabel ? `${matchLabel}` : "this option"}, and none were found for the same dates a year earlier to
							estimate from. Try a different {isHotel ? "check-in date" : "tee-time date"}.
						</div>
					) : filteredWindows.length === 0 ? (
						<div className="package-modal-empty">No packages match those filters - clear a filter to see more.</div>
					) : isHotel ? (
						filteredWindows.map((window) => <HotelPackageRow key={window.id} window={window} currencySymbol={currencySymbol} onSelect={() => onSelect({ window })} />)
					) : (
						filteredWindows.map((window) => <CoursePackageRow key={window.id} window={window} currencySymbol={currencySymbol} onSelect={(feeType) => onSelect({ window, feeType })} />)
					)}
				</div>
				</>
				)}
			</div>
		</div>
	)
}

function HotelPackageRow({ window, currencySymbol, onSelect }) {
	const isPerNight = window.type === "per_night"
	const rooms = (window.room_types || []).slice().sort((a, b) => (Number(a.cost) || 0) - (Number(b.cost) || 0))
	const boards = (window.board_types || []).slice().sort((a, b) => (Number(a.cost) || 0) - (Number(b.cost) || 0))

	return (
		<div className="package-row">
			<div className="package-row-head">
				<div className="package-row-titles">
					<Badge label={isPerNight ? "Per night" : "Pre defined"} type="SPECIAL" />
					{window.predicted && <Badge label={`Est. +${window.upliftPct}%`} type="AMBER" />}
					<p>{window.name?.replace(/&#039;/g, "'") || "Package"}</p>
				</div>
				<div className="package-row-price">
					{currencySymbol}
					{window.price_lowest}
					{window.price_highest && window.price_highest !== window.price_lowest ? ` - ${currencySymbol}${window.price_highest}` : ""}
					<small>pp</small>
				</div>
			</div>

			<div className="package-row-meta">
				<span>{isPerNight ? "Per night" : `${window.nights} nights`}</span>
				{window.rounds > 0 && <span>{window.rounds} rounds</span>}
				<span>{windowDateRange(window)}</span>
			</div>

			{rooms.length > 0 && (
				<div className="package-row-chips">
					{rooms.map((room) => (
						<span key={room._id || room.type} className="package-row-chip">
							{ROOM_TYPES[room.type] || room.type} · {currencySymbol}
							{room.cost}
						</span>
					))}
				</div>
			)}

			{boards.length > 0 && (
				<div className="package-row-chips is-board">
					{boards.map((board) => (
						<span key={board._id || board.type} className="package-row-chip">
							{BOARD_TYPES[board.type] || board.type}
							{Number(board.cost) > 0 ? ` · ${currencySymbol}${board.cost}` : ""}
						</span>
					))}
				</div>
			)}

			<div className="package-row-action">
				<Button small label="Select package" onClick={onSelect} />
			</div>
		</div>
	)
}

function CoursePackageRow({ window, currencySymbol, onSelect }) {
	const fees = (window.fee_types || []).slice().sort((a, b) => (Number(a.cost) || 0) - (Number(b.cost) || 0))

	return (
		<div className="package-row">
			<div className="package-row-head">
				<div className="package-row-titles">
					<Badge label="Green fees" type="POSITIVE" />
					{window.predicted && <Badge label={`Est. +${window.upliftPct}%`} type="AMBER" />}
					<p>{window.name?.replace(/&#039;/g, "'") || "Green fees"}</p>
				</div>
			</div>

			<div className="package-row-meta">
				<span>{windowDateRange(window)}</span>
			</div>

			{fees.length > 0 ? (
				<div className="package-row-fees">
					{fees.map((fee) => (
						<button key={fee._id || fee.type} type="button" className="package-fee" onClick={() => onSelect(fee.type)}>
							<span>{FEE_TYPES[fee.type] || fee.type}</span>
							<strong>
								{currencySymbol}
								{fee.cost}
							</strong>
						</button>
					))}
				</div>
			) : (
				<div className="package-row-action">
					<Button small label="Select package" onClick={() => onSelect("")} />
				</div>
			)}
		</div>
	)
}

/**
 * Hand-built one-off package, replicating the ad-hoc "enter pricing details"
 * block on the /quoting page. The agent sets the room mix + board (hotel) or fee
 * band (course), nights/rounds and the net costs; on add it's handed back
 * as a custom price window (via onAdd) that drives the basket item exactly like a
 * loaded one. Nights pre-fill from the trip; everything else is blank.
 *
 * Hotel net costs are PER ROOM TYPE (each chosen room row carries its own
 * per-person rate, alongside its quantity/sleeps); courses keep a single
 * per-person green-fee cost.
 */
function CustomPackageForm({ isHotel, currencySymbol, requestedNights, initial, onCancel, onAdd }) {
	// Editing an existing custom package seeds every field from `initial`; a fresh
	// build only pre-fills the nights from the trip.
	const [nights, setNights] = useState(initial ? String(initial.nights ?? "") : requestedNights ? String(requestedNights) : "")
	const [rounds, setRounds] = useState(initial && initial.rounds != null ? String(initial.rounds) : "")
	// The room mix: each entry { key, qty, sleeps, cost }. Several room types can
	// be chosen, each with its own quantity/sleeps (mirrors the hotel edit modal)
	// and its own per-person net cost.
	const [rooms, setRooms] = useState(
		initial?.rooms?.length
			? initial.rooms.map((room) => ({
					key: room.key,
					qty: Number(room.qty) || 1,
					sleeps: Number(room.sleeps) || defaultSleeps(room.key),
					cost: room.cost != null && room.cost !== "" ? String(room.cost) : "",
			  }))
			: []
	)
	const [boardType, setBoardType] = useState(initial?.boardType || "")
	const [feeType, setFeeType] = useState(initial?.feeType || "")
	const [netCost, setNetCost] = useState(initial && initial.netCost != null && initial.netCost !== "" ? String(initial.netCost) : "")
	// Bumped after each room add so the search-select remounts cleared, ready for
	// the next pick.
	const [roomPickerKey, setRoomPickerKey] = useState(0)

	const addRoom = (key) => {
		if (!key) return
		setRooms((current) => (current.some((room) => room.key === key) ? current : [...current, { key, qty: 1, sleeps: defaultSleeps(key), cost: "" }]))
		setRoomPickerKey((value) => value + 1)
	}
	const setRoomQty = (key, qty) => setRooms((current) => current.map((room) => (room.key === key ? { ...room, qty } : room)))
	const setRoomSleeps = (key, sleeps) => setRooms((current) => current.map((room) => (room.key === key ? { ...room, sleeps } : room)))
	const setRoomCost = (key, cost) => setRooms((current) => current.map((room) => (room.key === key ? { ...room, cost } : room)))
	const removeRoom = (key) => setRooms((current) => current.filter((room) => room.key !== key))

	// Room types not yet chosen, for the add-room search-select.
	const availableRooms = Object.fromEntries(Object.entries(ROOM_TYPES).filter(([key]) => !rooms.some((room) => room.key === key)))

	// Costs must be non-negative numbers. Hotels need at least one room and one
	// night, with a rate on EVERY chosen room; courses need a fee band + net cost.
	const costOk = (value) => value !== "" && !isNaN(Number(value)) && Number(value) >= 0
	const netCostOk = costOk(netCost)
	const roomCostsOk = rooms.length > 0 && rooms.every((room) => costOk(room.cost))
	const valid = isHotel ? roomCostsOk && Number(nights) > 0 : Boolean(feeType) && netCostOk

	const add = () => {
		if (!valid) return
		onAdd({ nights, rounds, rooms, boardType, feeType, netCost })
	}

	return (
		<>
			<div className="package-modal-body">
				<div className="package-custom-form">
					<p className="package-custom-intro">
						{isHotel
							? "Build a one-off package for this hotel - add the room types (with quantity, sleeps and the net cost per person for that room) and board, then set the nights and rounds."
							: "Enter a one-off green-fee rate for this course."}
					</p>

					{isHotel ? (
						<>
							<div className="package-custom-row">
								<Input type="number" label="Nights" showLabel placeholder="Nights" value={nights} onChange={setNights} />
								<Input type="number" label="Rounds" showLabel placeholder="Rounds" value={rounds} onChange={setRounds} />
							</div>

							<div className="package-custom-rooms-section">
								<span className="new-quote-rooms-label">Room types</span>
								<SearchSelect
									key={`room-add-${roomPickerKey}`}
									options={availableRooms}
									showLabel
									label="Add room type"
									placeholder="Search room type"
									onSelect={({ option }) => addRoom(option)}
								/>

								{rooms.length > 0 && (
									<div className="package-custom-rooms">
										{rooms.map((room) => (
											<div className="package-custom-room" key={room.key}>
												<label>{ROOM_TYPES[room.key] || room.key}</label>
												<div className="new-quote-room-controls">
													<div className="new-quote-room-ctrl">
														<span className="new-quote-room-ctrl-label">Qty</span>
														<Stepper value={room.qty} min={1} ariaLabel="quantity" onChange={(value) => setRoomQty(room.key, value)} />
													</div>
													<div className="new-quote-room-ctrl">
														<span className="new-quote-room-ctrl-label">Sleeps</span>
														<Stepper value={room.sleeps} min={1} ariaLabel="sleeps" onChange={(value) => setRoomSleeps(room.key, value)} />
													</div>
													<div className="new-quote-room-ctrl">
														<span className="new-quote-room-ctrl-label">Net pp</span>
														<Input
															wrapperClass="package-custom-room-price"
															type="number"
															symbol={currencySymbol}
															placeholder="0"
															value={room.cost}
															onChange={(value) => setRoomCost(room.key, value)}
														/>
													</div>
													<button type="button" className="package-custom-room-remove" onClick={() => removeRoom(room.key)} aria-label="Remove room type">
														<X size={15} />
													</button>
												</div>
											</div>
										))}
									</div>
								)}
							</div>

							<SearchSelect
								key={`board-${boardType}`}
								options={BOARD_TYPES}
								showLabel
								label="Board type"
								placeholder="Search board type (optional)"
								value={boardType ? BOARD_TYPES[boardType] : undefined}
								onSelect={({ option }) => setBoardType(option)}
							/>
						</>
					) : (
						<>
							<SearchSelect
								key={`fee-${feeType}`}
								options={FEE_TYPES}
								showLabel
								label="Fee type"
								placeholder="Search fee type"
								value={feeType ? FEE_TYPES[feeType] : undefined}
								onSelect={({ option }) => setFeeType(option)}
							/>
							<Input type="number" label="Net cost per person" showLabel symbol={currencySymbol} placeholder="Net cost per person" value={netCost} onChange={setNetCost} note="The per-person green-fee net cost" />
						</>
					)}
				</div>
			</div>

			<div className="package-modal-foot">
				<Button type="secondary" small label="Cancel" onClick={onCancel} />
				<Button small label={initial ? "Update custom package" : "Add custom package"} disabled={!valid} onClick={add} />
			</div>
		</>
	)
}
